import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, ChevronRight, CheckCircle2, Package, Truck, Megaphone, Award, BarChart3, RefreshCw, Target, TrendingUp } from 'lucide-react';

const SwiggyAccountManagement = () => {
  const services = [
    {
      title: 'Listing Optimization',
      icon: Package,
      items: ['Product title optimization', 'Description enhancement', 'Image management', 'Category mapping', 'SEO optimization']
    },
    {
      title: 'Inventory Management',
      icon: Truck,
      items: ['Stock level monitoring', 'Replenishment planning', 'Demand forecasting', 'Out-of-stock prevention']
    },
    {
      title: 'Advertising & Growth',
      icon: Megaphone,
      items: ['Campaign setup & management', 'Performance optimization', 'Promotional campaigns', 'ROI tracking']
    }
  ];

  const whyChooseUs = [
    { icon: Award, title: 'Expert Management', description: 'Dedicated account managers with Swiggy expertise' },
    { icon: BarChart3, title: 'Data-Driven Strategies', description: 'Analytics-based decision making' },
    { icon: RefreshCw, title: 'Continuous Optimization', description: 'Regular performance improvements' },
    { icon: TrendingUp, title: 'Growth Focused', description: 'Strategies designed to scale your business' }
  ];

  return (
    <div className="min-h-screen pt-32">
      <Helmet>
        <title>Swiggy Account Management & Growth Services | Obiquiste</title>
        <meta name="description" content="Professional Swiggy account management: listing optimization, advertising, inventory & growth strategies. Scale your Swiggy business with our experts." />
        <link rel="canonical" href="https://www.obiquistesolutions.com/swiggy-account-management" />
      </Helmet>
      {/* Breadcrumb */}
      <div className="bg-slate-50 py-4">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center text-sm text-slate-600">
            <Link to="/" className="hover:text-primary">Home</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <Link to="/services" className="hover:text-primary">Services</Link>
            <ChevronRight className="w-4 h-4 mx-2" />
            <span className="text-orange-600 font-medium">Swiggy Account Management</span>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-orange-50 to-yellow-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="max-w-4xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center bg-orange-100 text-orange-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <TrendingUp className="w-4 h-4 mr-2" />
                Growth & Management
              </div>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                Swiggy <span className="text-orange-600">Account Management</span>
              </h1>
              <p className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed">
                Professional account management to scale your brand on Swiggy. 
                From listing optimization to advertising, we drive your growth.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact?audit=true">
                  <Button className="bg-orange-600 text-white hover:bg-orange-700 px-8 py-6 rounded-full text-base font-semibold">
                    Get Started
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-center mb-12">
            Our Services
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg border border-slate-200"
              >
                <div className="w-14 h-14 bg-orange-100 rounded-xl flex items-center justify-center mb-6">
                  <service.icon className="w-7 h-7 text-orange-600" />
                </div>
                <h3 className="font-heading text-xl font-bold mb-4">{service.title}</h3>
                <ul className="space-y-3">
                  {service.items.map((item, idx) => (
                    <li key={idx} className="flex items-start">
                      <CheckCircle2 className="w-5 h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-slate-600">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-center mb-12">
            Why Choose Us?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-md text-center"
              >
                <item.icon className="w-12 h-12 text-orange-600 mx-auto mb-4" />
                <h3 className="font-heading font-bold text-lg mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-orange-600 to-yellow-500">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Scale on Swiggy?
          </h2>
          <p className="text-orange-100 text-lg mb-8 max-w-2xl mx-auto">
            Let our experts manage your Swiggy account and accelerate your growth.
          </p>
          <Link to="/contact?audit=true">
            <Button className="bg-white text-orange-600 hover:bg-slate-100 px-8 py-6 rounded-full text-base font-semibold">
              Get Free Consultation
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default SwiggyAccountManagement;
